import React, { useState, useEffect } from "react";
import usePageTitle from '../../hooks/usePageTitle';
import { usePartner } from '../../context/PartnerContext';
import { Link, useNavigate, useSearchParams } from "react-router-dom";

export default function ResetPassword() {
  usePageTitle("Reset Password");
  const { appName, logoUrl, partner, loading: brandLoading, tagline } = usePartner();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [validToken, setValidToken] = useState(false);
  const navigate = useNavigate();
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) { setError("Invalid reset link."); return; }
    fetch(`/api/auth/reset-password/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.valid) setValidToken(true);
        else setError("Reset link is invalid or expired.");
      })
      .catch(() => setError("Failed to validate reset link."));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setBusy(true);
    setError("");
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to reset password");
      setStatus("Password reset successful. Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const EyeIcon = ({ open }) => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
      {open ? (
        <><path d="M3 3l14 14M10 4C5.6 4 2 10 2 10s1.4 2.2 3.8 4M10 16c4.4 0 8-6 8-6s-1.4-2.2-3.8-4"/><circle cx="10" cy="10" r="2.5"/></>
      ) : (
        <><path d="M2 10s3.6-6 8-6 8 6 8 6-3.6 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="2.5"/></>
      )}
    </svg>
  );

  return (
    <div className="auth-shell">
      <div className="auth-panel">
        <div className="auth-brand">
          <div className="auth-brand-logo" style={{display:"flex",alignItems:"center",gap:8,justifyContent:"center"}}>{(logoUrl || (!partner && !brandLoading)) && <img src={logoUrl || "/logo.jpeg"} alt={appName || "CrunchAlpha"} style={{height:32,width:32,borderRadius:6,objectFit:"cover"}} onError={e=>e.target.style.display="none"}/>} {appName || "CrunchAlpha"}</div>
          {(partner ? tagline : "Risk Controlled Copy Trading") && <div className="auth-brand-tagline">{partner ? tagline : "Risk Controlled Copy Trading"}</div>}
        </div>
        <div className="auth-card">
          <h2>Reset password</h2>
          <p className="auth-subtitle">Enter your new password below.</p>
          {status && (
            <div style={{padding:"10px 14px",borderRadius:"var(--radius-md)",
              border:"1px solid rgba(34,197,94,0.4)",background:"var(--success-soft)",
              color:"var(--success)",fontSize:13,marginBottom:16}}>
              {status}
            </div>
          )}
          {error && <div className="auth-error">{error}</div>}
          {!validToken && !error && (
            <p style={{fontSize:13,color:"var(--text-faint)"}}>Validating reset link...</p>
          )}
          {validToken && (
            <form onSubmit={handleSubmit} className="auth-form">
              <label>
                <span>New password</span>
                <div style={{position:"relative"}}>
                  <input
                    type={showPassword ? "text" : "password"}
                    required value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min. 8 characters" minLength={8}
                    style={{paddingRight:"44px"}}
                  />
                  <button type="button" tabIndex={-1}
                    onClick={() => setShowPassword(v => !v)}
                    style={{position:"absolute",right:"12px",top:"50%",transform:"translateY(-50%)",
                      background:"none",border:"none",cursor:"pointer",color:"var(--text-faint)",
                      padding:"2px",display:"flex",alignItems:"center"}}>
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
                {password.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                      {[password.length >= 8, /[A-Z]/.test(password), /[a-z]/.test(password), /[0-9]/.test(password)].map((ok, i) => (
                        <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: ok ? "var(--accent)" : "var(--border)" }} />
                      ))}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-faint)", display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
                      {[
                        { label: "8+ chars", ok: password.length >= 8 },
                        { label: "Uppercase", ok: /[A-Z]/.test(password) },
                        { label: "Lowercase", ok: /[a-z]/.test(password) },
                        { label: "Number", ok: /[0-9]/.test(password) },
                      ].map(r => (
                        <span key={r.label} style={{ color: r.ok ? "var(--accent)" : "var(--text-faint)" }}>
                          {r.ok ? "" : "○"} {r.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </label>
              <label>
                <span>Confirm password</span>
                <div style={{position:"relative"}}>
                  <input
                    type={showConfirm ? "text" : "password"}
                    required value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Repeat password"
                    style={{paddingRight:"44px"}}
                  />
                  <button type="button" tabIndex={-1}
                    onClick={() => setShowConfirm(v => !v)}
                    style={{position:"absolute",right:"12px",top:"50%",transform:"translateY(-50%)",
                      background:"none",border:"none",cursor:"pointer",color:"var(--text-faint)",
                      padding:"2px",display:"flex",alignItems:"center"}}>
                    <EyeIcon open={showConfirm} />
                  </button>
                </div>
              </label>
              <button type="submit" className="auth-button" disabled={busy}>
                {busy ? "Resetting..." : "Reset password"}
              </button>
            </form>
          )}
        </div>
        <p className="auth-footer">
          <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
